"use client";

import { m as motion, useMotionValue, useSpring } from "framer-motion";
import { useEffect } from "react";

import { useCanHover } from "@/hooks/useCanHover";
import { useMatchMedia } from "@/hooks/useMatchMedia";

export function CustomCursor() {
  // Skip the custom cursor on touch devices (no fine pointer) AND when
  // the user has asked for reduced motion — the spring trail is exactly
  // the kind of background motion the preference is meant to disable.
  // Both hooks return null on the server and during the first client
  // render, so `enabled` stays false until matchMedia results land.
  const canHover = useCanHover();
  const reducedMotion = useMatchMedia("(prefers-reduced-motion: reduce)");
  const enabled = canHover === true && reducedMotion === false;

  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const springX = useSpring(x, { stiffness: 420, damping: 32, mass: 0.35 });
  const springY = useSpring(y, { stiffness: 420, damping: 32, mass: 0.35 });

  useEffect(() => {
    if (!enabled) return;

    function onMove(e: PointerEvent) {
      x.set(e.clientX);
      y.set(e.clientY);
    }

    window.addEventListener("pointermove", onMove, { passive: true });
    return () => window.removeEventListener("pointermove", onMove);
  }, [enabled, x, y]);

  if (!enabled) return null;

  return (
    <motion.div
      aria-hidden
      className="pointer-events-none fixed left-0 top-0 z-[9999] h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full mix-blend-difference"
      style={{
        x: springX,
        y: springY,
        backgroundColor: "#F5F5F7",
      }}
    />
  );
}
